// Types
import type {
  TStoredFilters,
  TWrappedFilters,
  TFilterTimestamps,
  TFilterField,
  TUnwrappedFilters,
  TListingFilterLocation,
  EMarketingConsent,
  EContactType,
  IRange,
} from '~/misc/listings/types';
import type { TOptionalToNullable } from '~/shared/utils/types';

// Utils
import { DateTimeFormatter } from '~/misc/utils/datetime';

/** Type guard — checks whether a value has the `{ value, updated_at }` shape. */
const isFilterField = (val: unknown): val is TFilterField<unknown> =>
  val !== null && typeof val === 'object' && 'value' in val && 'updated_at' in val;

/** Key-order-independent JSON.stringify for deep comparison. Treats undefined as null. */
const stableStringify = (val: unknown): string =>
  JSON.stringify(val ?? null, (_, v) =>
    v !== null && typeof v === 'object' && !Array.isArray(v)
      ? Object.fromEntries(Object.entries(v).sort(([a], [b]) => a.localeCompare(b)))
      : v
  );

/**
 * Extracts flat filter values and per-field timestamps from a wrapped
 * API response (`{ key: { value, updated_at } }`).
 *
 * The `type` key is intentionally excluded from the returned filters
 * because sale type is managed separately.
 *
 * @param wrapped - Wrapped filter response from the API.
 * @returns filters    - Flat key-value filter pairs.
 * @returns timestamps - Per-key `updated_at` strings.
 */
const unwrapFilters = (wrapped: TWrappedFilters): TUnwrappedFilters => {
  const filters: Record<string, unknown> = {};
  const timestamps: TFilterTimestamps = {};
  const keys = Object.keys(wrapped) as (keyof TWrappedFilters)[];

  for (const key of keys) {
    const field = wrapped[key];

    if (!isFilterField(field)) continue;

    timestamps[key] = field.updated_at;

    if (key !== 'type') {
      filters[key] = field.value;
    }
  }

  return { filters: filters as TStoredFilters, timestamps };
};

/**
 * Wraps flat filter values into `{ value, updated_at }` fields for the API.
 *
 * Change detection uses `stableStringify` for key-order-independent deep
 * comparison. Changed fields receive a new ISO timestamp; unchanged fields
 * keep their original timestamp from `timestamps`.
 *
 * @param current    - Current flat filter values to wrap.
 * @param timestamps - Per-key timestamps from the last unwrap or save.
 * @param previous   - Previous flat values for change detection (null = all changed).
 * @param opts.onlyChanged - When true, unchanged fields are omitted from the result.
 */
const wrapFilters = (
  current: TStoredFilters,
  timestamps: TFilterTimestamps,
  previous: TStoredFilters | null,
  opts: { onlyChanged?: boolean } = {}
): TWrappedFilters => {
  const wrapped: Record<string, TFilterField<unknown>> = {};
  const keys = Object.keys(current) as (keyof TStoredFilters)[];
  const now: string = DateTimeFormatter.toIso8601(new Date());

  for (const key of keys) {
    const value:
      | string
      | number
      | number[]
      | TListingFilterLocation
      | EMarketingConsent[]
      | EContactType[]
      | TOptionalToNullable<IRange>
      | null
      | undefined = current[key];
    const isChanged: boolean =
      !previous || stableStringify(current[key]) !== stableStringify(previous[key]);

    if (opts.onlyChanged && !isChanged) continue;

    wrapped[key] = {
      value,
      updated_at: isChanged ? now : timestamps[key] ?? now,
    };
  }

  return wrapped as TWrappedFilters;
};

/**
 * Unwraps company defaults that may arrive in wrapped `{ value, updated_at }`
 * format. Values already in flat format are passed through unchanged.
 */
const unwrapDefaults = (defaults: Record<string, unknown>): TStoredFilters => {
  const result: Record<string, unknown> = {};

  for (const [key, val] of Object.entries(defaults)) {
    result[key] = isFilterField(val) ? val.value : val;
  }

  return result as TStoredFilters;
};

export { unwrapFilters, wrapFilters, unwrapDefaults };
